import { rankDemoGaps, MIN_GAP } from "./demoGaps";

// Scenario planning calc - pure functions, no React or API call
// Mirrors backend BEE level rules in scorecard_totals and yes_engine --> if rules change, changes need to be made here too


// targetMultiple / minAbsorptionPct mirror _apply_yes_recognition in yes_engine
export const YES_TIERS = {
    none:       { label: 'No YES recognition',                          levelsUp: 0, bonusPoints: 0, targetMultiple: 0,   minAbsorptionPct: 0 },
    tier1:      { label: 'Tier 1 - target met (+1 level)',               levelsUp: 1, bonusPoints: 0, targetMultiple: 1,   minAbsorptionPct: 2.5 },
    tier1_5:    { label: 'Tier 1.5 - 1.5x target (+1 level, +3 pts)',   levelsUp: 1, bonusPoints: 3, targetMultiple: 1.5, minAbsorptionPct: 5 },
    tier2:      { label: 'Tier 2 - 2x target (+2 levels)',              levelsUp: 2, bonusPoints: 0, targetMultiple: 2,   minAbsorptionPct: 5 },
};

// Floating-point maths can land on 79.9999 instead of 80 - allow a tiny tolerance
const EPS = 1e-9;

const round2 = (n) => Math.round(n * 100) / 100;
const ceil2 = (n) => Math.ceil(n * 100 - EPS) / 100;

export const itemKey = (elementId, indicatorId) => `${elementId}::${indicatorId}`;

const forEachElement = (baseline, fn) => 
    baseline.elements.forEach((group) => group.elements.forEach(fn));

const addedFor = (additions, elementId, indicatorId) => additions[itemKey(elementId, indicatorId)] || 0;

export const totalAdded = (additions) =>
    Object.values(additions).reduce((sum, v) => sum + (Number(v) || 0), 0);

// ------------- LEVELS ------------------------------------------------------------------------------------

export const rawLevel = (points, thresholds) => {       // rawLevel is using the sector's thresholds
    const hit = thresholds.find((t) => points >= t.min - EPS);
    return hit ? hit.label : 'Non-Compliant';
};

const minPointsFor = (label, thresholds) => thresholds.find((t) => t.label === label)?.min ?? 0;

// Positive n = worse (sub-min discount), negative n = better (YES uplift)
export const shiftLevel = (level, n, order) => {    // shiftLevel moves a level up or down
    const idx = Math.min(Math.max(order.indexOf(level) + n, 0), order.length - 1);
    return order[idx];
};

// ------------- ROWS ---------------------------------------------------------------------------------------

// One flat list of every indicator the user can add points too
export const flattenIndicators = (baseline) => {        // Nested backend data turned into 1 list of rows
    const items = [];
    baseline.elements.forEach((group) => group.elements.forEach((el) => {
        el.indicators.forEach((ind) => items.push({
            ...ind,
            key: itemKey(el.id, ind.id),
            elementId: el.id,
            elementLabel: el.label,
            groupKey: group.key,
            priorityGroup: el.priority_group,
            room: Math.max(ind.max_points - ind.current_points, 0),
        }));
    }));
    return items;
};

export const clampAddition = (item, value) => {     // clampAddition stops user from entering more points than allowed by indicator
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 0;
    if (item.wholeOnly) return Math.min(Math.round(n), item.room);
    return Math.min(round2(n), item.room);
};

// ------------ SUB-MINIMUMS -----------------------------------------------------------------------------------------

export const elementSubMinMet = (el, additions, sectorSubMinPct) => {
    if (!el.calculated || !el.priority_group) return true;      // Backend treats "not calculated" as passing
    if (el.sub_minimum_met !== false) return true;              // added points only go up, so a pass stays a pass

    if (el.id === 'ownership') {
        // Ownership's flag comes from Net Value's own sub-min only
        return el.indicators
            .filter((i) => i.sub_minimum_pct != null)
            .every((i) => i.current_points + addedFor(additions, el.id, i.id) >= i.sub_minimum_pct * i.max_points - EPS);
    }

    if (sectorSubMinPct == null) return true;
    const nonBonus = el.indicators.filter((i) => !i.bonus);
    const max = nonBonus.reduce((s, i) => s + i.max_points, 0);
    const scored = nonBonus.reduce((s, i) => s + i.current_points + addedFor(additions, el.id, i.id), 0);
    return scored >= sectorSubMinPct * max - EPS;
};

export const subMinStatus = (baseline, additions) => {
    const status = { ownership: true, skills: true, esd: true };
    forEachElement(baseline, (el) => {
        if (el.priority_group && !elementSubMinMet(el, additions, baseline.sub_minimum_pct)) {
            status[el.priority_group] = false;
        }
    });
    return status;
};

// ------------- RESULT ----------------------------------------------------------------------------------------------

// YES only counts if the pre-YES level holds the level recorded on Company Info
const priorLevelMaintained = (baseline, levelBeforeYes) => {
    const order = baseline.level_order;
    const base = baseline.yes?.baseline_level;
    if (!order.includes(base)) return true;     // nothing recorded - the backend doesn't block either
    return order.indexOf(levelBeforeYes) <= order.indexOf(base);
};

export const scenarioResult = (baseline, additions, yesTier = 'none') => {
    const { thresholds, level_order: order } = baseline;
    const points = round2(baseline.current.points + totalAdded(additions));
    const subMin = subMinStatus(baseline, additions);
    const nFailures = Object.values(subMin).every(Boolean) ? 0: 1;
    const raw = rawLevel(points, thresholds);
    const levelBeforeYes = shiftLevel(raw, nFailures, order);

    const tier = YES_TIERS[yesTier] || YES_TIERS.none;
    const yesBlocked = tier.levelsUp > 0 && !priorLevelMaintained(baseline, levelBeforeYes);
    const applied = yesBlocked ? YES_TIERS.none : tier;

    return {
        points,
        subMin,
        nFailures,
        rawLevel: raw,
        levelBeforeYes,
        level: shiftLevel(levelBeforeYes, -applied.levelsUp, order),
        finalPoints: round2(points + applied.bonusPoints),
        yesBlocked,
    };
};

// Minimum raw points for the raw level at this index (null = impossible)
const rawPointsForIdx = (idx, baseline) => {
    if (idx < 0) return null;
    const order = baseline.level_order;
    return minPointsFor(order[Math.min(idx, order.length - 1)], baseline.thresholds);
};


export const shortfall = (baseline, additions, targetLevel, yesTier = 'none') => {
    const order = baseline.level_order;
    const now = scenarioResult(baseline, additions, yesTier);
    const t = order.indexOf(targetLevel);
    const up = YES_TIERS[yesTier]?.levelsUp || 0;
    const nF = now.nFailures;

    // Option 1 -> reach target on points alone
    const withoutYES = rawPointsForIdx(t - nF, baseline);

    // Option 2 -> with YES - the pre-YES level must also hold the recorded baseline level
    let withYes = null;
    if (up > 0) {
        withYes = rawPointsForIdx(t + up - nF, baseline);
        const base = baseline.yes?.baseline_level;
        if (withYes != null && order.includes(base)) {
            const holdBase = rawPointsForIdx(order.indexOf(base) - nF, baseline);
            withYes = holdBase == null ? null : Math.max(withYes, holdBase);
        }
    }

    const options = [withoutYES, withYes].filter((p) => p != null);
    const requiredPoints = options.length ? Math.min(...options) : null;

    return {
        requiredPoints,         // raw (pre-YES) points needed
        pointsNeeded: requiredPoints == null ? null : Math.max(round2(requiredPoints - now.points), 0),
        blockedBySubMin: requiredPoints == null,        // can't get there until a sub-minimum is fixed
        reached: order.indexOf(now.level) <= t,
    };
};

// --------------- AUTO-SUGGEST --------------------------------------------------------------

// Rand needed per point for spend-based indicators -> null for everything else
export const costPerPoint = (item) => {
    const { kind, denominator } = item.translate || {};
    if (kind !== 'spend' || !item.target || !item.max_points) return null;
    return (denominator * item.target / 100) / item.max_points;
};

export const sortItems = (items, mode) => [...items].sort((a, b) => {

    if (mode === 'rand') {
        const ca = costPerPoint(a);
        const cb = costPerPoint(b);
        if (ca != null && cb != null && ca !== cb) return ca - cb;
        if (ca != null && cb == null) return -1;
        if (ca == null && cb != null) return 1;
    }
    return b.room - a.room;
});

// Adds up to 'amount' to an item, respecting its remaining room. Returns what was actually added
const addTo = (additions, item, amount) => {
    const current = additions[item.key] || 0;
    const roomLeft = round2(item.room - current);
    let add = Math.min(Math.max(amount, 0), roomLeft);
    if (item.wholeOnly) add = add > 0 ? Math.min(Math.ceil(add - EPS), roomLeft) : 0;
    if (add <= 0) return 0;
    additions[item.key] = round2(current + add);
    return add;
};

export const autoAllocate = (baseline, targetLevel, { mode = 'gap', locked = [], yesTier = 'none' } = {}) => {
    const lockedSet = new Set(locked);
    const additions = {};
    const items = sortItems(
        flattenIndicators(baseline).filter((i) => !lockedSet.has(i.key) && i.room > 0),
        mode,
    );
    
    if (shortfall(baseline, additions, targetLevel, yesTier).reached) {
        return { additions, reached: true };
    }

    // 1 - Fix fialed submin first -> each failure costs a whole level
    forEachElement(baseline, (el) => {
        if (elementSubMinMet(el, additions, baseline.sub_minimum_pct)) return;
        const own = items.filter((i) => i.elementId === el.id && !i.bonus);

        if (el.id === 'ownership') {
            own.filter((i) => i.sub_minimum_pct != null).forEach((i) => {
                addTo(additions, i, ceil2(i.sub_minimum_pct * i.max_points - i.current_points));
            });
            return;
        }

        const nonBonus = el.indicators.filter((i) => !i.bonus);
        const max = nonBonus.reduce((s, i) => s + i.max_points, 0);
        const scored = nonBonus.reduce((s, i) => s + i.current_points + addedFor(additions, el.id, i.id), 0);
        let need = ceil2(baseline.sub_minimum_pct * max - scored);
        own.forEach((i) => {
            if (need > 0) need = round2(need - addTo(additions, i, need));
        });
    });

    // 2 - Fill the rest of the shortfall in the chosen order
    for (const item of items) {
        const { pointsNeeded, reached } = shortfall(baseline, additions, targetLevel, yesTier);
        if (reached || pointsNeeded == null || pointsNeeded <= 0) break;
        addTo(additions, item, ceil2(pointsNeeded));
    }

    return { additions, reached: shortfall(baseline, additions, targetLevel, yesTier).reached };
};

// ----------- REAL-WORLD TRANSLATION ---------------------------------------------------------------

// Biggest gap first (same order as the recommendations tips) -> leftover points go to the next group
const splitByDemographic = (demographics, addedPoints, kind) => {
    let left = addedPoints;
    const breakdown = [];
    rankDemoGaps(demographics).forEach((g) => {
        if (left <= 0 || g.gap <= MIN_GAP) return;
        const pts = Math.min(g.gap, left);
        left -= pts;
        const share = pts / g.max_points;
        const value = kind === 'spend' ? Math.round(share * g.target) : Math.ceil(share * g.target - EPS);
        if (value > 0) breakdown.push({ key: g.key, value});
    });
  
    return breakdown.length ? breakdown : null;
};

// Linear indicators only - added/max_points x target %  x denominator
export const toRealWorld = (item, addedPoints) => {         // 3 parts of this indicator. Standard scoring formula working in reverse
    const { kind, denominator, demographics } = item.translate || {};
    if (!addedPoints || !item.max_points || item.target == null) return null;
    const pctOfBase = (addedPoints / item.max_points) * item.target;
    const breakdown = demographics?.length ? splitByDemographic(demographics, addedPoints, kind) : null;

    if (kind === 'pct') return { kind, value: round2(pctOfBase) };
    if (kind === 'spend') return { kind, value: Math.round((pctOfBase / 100) * denominator), ...(breakdown && { breakdown }) };
    if (kind === 'headcount_estimate') {
        return { kind, value: Math.ceil((pctOfBase / 100) * denominator - EPS), estimate: true, ...(breakdown && { breakdown }) };
    }
    return null;
};

// ------- ACTION SUMMARY ------------------------------------------------------------------------------

// Every action the scenario needs, grouped by element in backend order, plus totals,
// sub-minimum changes and the Y.E.S placements needed for the selected tier
export const actionSummary = (baseline, additions, yesTier = 'none') => {
    const elements = [];
    const totals = { points: totalAdded(additions), rand: 0, people: 0 };

    flattenIndicators(baseline).forEach((item) => {
        const points = additions[item.key] || 0;
        if (points <= 0) return;
        const rw = toRealWorld(item, points);
        if (rw?.kind === 'spend') totals.rand += rw.value;
        if (rw?.kind === 'headcount_estimate') totals.people += rw.value;

        let el = elements[elements.length - 1];
        if (!el || el.id !== item.elementId) {
            el = { id: item.elementId, label: item.elementLabel, actions: [] };
            elements.push(el);
        }
        el.actions.push({ key: item.key, label: item.label, points, realWorld: rw, bonus: Boolean(item.bonus) });
    });

    const before = subMinStatus(baseline, {});
    const after = subMinStatus(baseline, additions);
    const groups = Object.keys(after);
    const subMin = {
        fixed: groups.filter((g) => !before[g] && after[g]),
        failing: groups.filter((g) => !after[g]),
    };

    const tier = YES_TIERS[yesTier] || YES_TIERS.none;
    const yes = baseline.yes;
    let yesAction = null;
    if (yes && tier.levelsUp > 0) {
        const required = Math.ceil(yes.target * tier.targetMultiple - EPS);
        yesAction = {
            tier: yesTier,
            register: !yes.registered,
            required,
            current: yes.eligible_placements,
            more: Math.max(required - yes.eligible_placements, 0),
            minAbsorptionPct: tier.minAbsorptionPct,
            absorptionPct: round2(yes.absorption_pct || 0),
        };
    }

    return {
        elements,
        totals: { ...totals, points: round2(totals.points) },
        subMin,
        yes: yesAction,
        result: scenarioResult(baseline, additions, yesTier),
    };
};

// ------- DROPDOWN HELPERS ---------------------------------------------------------------------------

// Levels better than the current one, next level increase first
export const targetOptions = (baseline, currentLevel) => {
    const order = baseline.level_order;
    return order.slice(0, order.indexOf(currentLevel)).reverse();
};

// Starts the YES picker on whatever tier the company currently achieves
export const defaultYesTier = (baseline) => {
    const yes = baseline.yes;
    if (!yes || !yes.current_tier) return 'none';
    if (yes.current_tier >= 2) return 'tier2';
    return yes.current_bonus_points > 0 ? 'tier1_5' : 'tier1';
};